import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import planService, { CouponValidation, Plan } from '@/services/plan.service';
import { useAuth } from '@/contexts/auth.context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DuplicateDocumentDialog, DuplicateDocumentInfo } from '@/components/checkout/DuplicateDocumentDialog';
import { POST_LOGIN_ROUTE } from '@/lib/auth-routes';
import { 
  ArrowLeft, 
  Shield, 
  Tag, 
  CheckCircle,
  AlertCircle,
  Loader2,
  CreditCard,
  MapPin,
  User,
  Building2,
  Receipt,
  Lock
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import authService, { CheckoutDataRequest } from '@/services/auth.service';
import { cn } from '@/lib/utils';
import { BRAND_HERO_GRADIENT } from '@/lib/brand';
import { PHONE_INPUT_MAX_LENGTH, applyPhoneMask } from '@/lib/phone';

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user, refreshProfile } = useAuth();
  const { plan, email: stateEmail, name: stateName, billingCycle } = (state || {}) as { 
    plan: Plan; 
    email?: string; 
    name?: string; 
    billingCycle?: 'monthly' | 'yearly';
  };
  
  // Use auth context data or state data
  const email = user?.email || stateEmail;
  const name = user?.name || stateName;
  
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState<CouponValidation | null>(null);
  const [validating, setValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Estados para modal de documento duplicado
  const [showDuplicateDocumentModal, setShowDuplicateDocumentModal] = useState(false);
  const [duplicateDocumentInfo, setDuplicateDocumentInfo] = useState<DuplicateDocumentInfo | null>(null);
  
  // Estados para os dados adicionais do usuário
  const [formData, setFormData] = useState({
    personType: 'fisica' as 'fisica' | 'juridica',
    cpf: '',
    cnpj: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    billingData: {
      companyName: '',
      email: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: ''
    }
  });
  const [cardData, setCardData] = useState({
    holder: '',
    number: '',
    expiry: '',
    cvv: '',
  });

  // Funções de máscara
  const applyCPFMask = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+$/, '$1');
  };

  const applyCNPJMask = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .replace(/(-\d{2})\d+$/, '$1');
  };

  const applyCEPMask = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{3})\d+$/, '$1');
  };

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 19);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  const formatCVV = (value: string) => value.replace(/\D/g, '').slice(0, 4);

  const handleCardInputChange = (field: 'holder' | 'number' | 'expiry' | 'cvv', value: string) => {
    if (field === 'number') {
      setCardData((prev) => ({ ...prev, number: formatCardNumber(value) }));
    } else if (field === 'expiry') {
      setCardData((prev) => ({ ...prev, expiry: formatExpiry(value) }));
    } else if (field === 'cvv') {
      setCardData((prev) => ({ ...prev, cvv: formatCVV(value) }));
    } else {
      setCardData((prev) => ({ ...prev, holder: value }));
    }
  };

  const detectCardBrand = (digits: string) => {
    if (digits.startsWith('4')) return 'visa';
    if (/^5[1-5]/.test(digits)) return 'mastercard';
    if (/^3[47]/.test(digits)) return 'amex';
    if (/^6(?:011|5)/.test(digits)) return 'discover';
    if (/^3(?:0[0-5]|[68])/.test(digits)) return 'diners';
    return 'desconhecida';
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'cpf') {
      value = applyCPFMask(value);
    } else if (field === 'cnpj') {
      value = applyCNPJMask(value);
    } else if (field === 'zipCode') {
      value = applyCEPMask(value);
    } else if (field.includes('phone')) {
      value = applyPhoneMask(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBillingInputChange = (field: string, value: string) => {
    if (field === 'zipCode') {
      value = applyCEPMask(value);
    } else if (field === 'phone') {
      value = applyPhoneMask(value);
    }

    setFormData(prev => ({
      ...prev,
      billingData: {
        ...prev.billingData,
        [field]: value
      }
    }));
  };

  useEffect(() => {
    if (!plan) {
      navigate('/register/plans');
    }
  }, [plan, navigate]);

  useEffect(() => {
    if (!plan?.require_card_on_signup) {
      setCardData({ holder: '', number: '', expiry: '', cvv: '' });
    }
  }, [plan]);

  const { total, discountAmount, recurringAmount } = useMemo(() => {
    if (!plan) return { total: 0, discountAmount: 0, recurringAmount: 0 };
    
    const baseRecurringAmount = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
    let base = baseRecurringAmount + (plan.setup_fee || 0);
    let discount = 0;
    
    if (coupon?.valid && coupon.data) {
      if (coupon.data.discount_type === 'percentage') {
        discount = Math.round((base * (coupon.data.discount_value / 100) + Number.EPSILON) * 100) / 100;
      } else if (coupon.data.discount_type === 'fixed') {
        discount = Math.min(coupon.data.discount_value, base);
      }

      discount = Math.round((discount + Number.EPSILON) * 100) / 100;
      base = Math.max(Math.round(((base - discount) + Number.EPSILON) * 100) / 100, 0);
    }
    
    return { 
      total: Math.max(base, 0), 
      discountAmount: discount,
      recurringAmount: baseRecurringAmount,
    };
  }, [plan, coupon, billingCycle]);

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      toast({
        title: "Erro",
        description: "Digite um código de cupom",
        variant: "destructive",
      });
      return;
    }
    
    setValidating(true);
    try {
      const result = await planService.validateCoupon(couponCode.trim().toUpperCase(), email, plan?.id, billingCycle || 'monthly');
      setCoupon(result);
      
      if (result.valid) {
        toast({
          title: "Cupom aplicado!",
          description: `Desconto de ${result.data?.discount_type === 'percentage' ? result.data.discount_value + '%' : 'R$ ' + result.data?.discount_value} aplicado com sucesso.`,
        });
      } else {
        toast({
          title: "Cupom inválido",
          description: result.reason || "Verifique o código e tente novamente",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Erro ao validar cupom",
        description: "Tente novamente mais tarde",
        variant: "destructive",
      });
    } finally {
      setValidating(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponCode('');
    toast({
      title: "Cupom removido",
      description: "O desconto foi removido do seu pedido",
    });
  };

  const proceed = async () => {
    setIsProcessing(true);
    
    try {
      // Validações obrigatórias
      if (!formData.cpf) {
        toast({
          title: "CPF obrigatório",
          description: "Por favor, informe o CPF do responsável",
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }
      
      if (formData.personType === 'juridica' && !formData.cnpj) {
        toast({
          title: "CNPJ obrigatório",
          description: "Para pessoa jurídica, o CNPJ é obrigatório",
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      if (formData.personType === 'juridica') {
        if (!formData.billingData.companyName) {
          toast({
            title: "Razão Social obrigatória",
            description: "Por favor, informe a Razão Social da empresa",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }
        
        if (!formData.billingData.email) {
          toast({
            title: "E-mail da empresa obrigatório",
            description: "Por favor, informe o e-mail da empresa",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }
      }

      let paymentMethodPayload: CheckoutDataRequest['payment_method'];

      if (plan.require_card_on_signup) {
        const digits = cardData.number.replace(/\D/g, '');
        const expiryDigits = cardData.expiry.replace(/\D/g, '');
        const cvvDigits = cardData.cvv.replace(/\D/g, '');

        if (!cardData.holder.trim()) {
          toast({
            title: "Nome do cartão obrigatório",
            description: "Informe o nome exatamente como está impresso no cartão",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }

        if (digits.length < 13 || digits.length > 19) {
          toast({
            title: "Número do cartão inválido",
            description: "Verifique o número do cartão informado",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }

        if (expiryDigits.length !== 4) {
          toast({
            title: "Validade inválida",
            description: "Informe a validade no formato MM/AA",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }

        const expMonth = Number.parseInt(expiryDigits.slice(0, 2), 10);
        const expYearRaw = expiryDigits.slice(2);
        const expYear = expYearRaw.length === 2 ? 2000 + Number.parseInt(expYearRaw, 10) : Number.parseInt(expYearRaw, 10);

        if (Number.isNaN(expMonth) || expMonth < 1 || expMonth > 12) {
          toast({
            title: "Mês de validade inválido",
            description: "Informe um mês entre 01 e 12",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }

        if (Number.isNaN(expYear)) {
          toast({
            title: "Ano de validade inválido",
            description: "Informe um ano válido",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }

        const today = new Date();
        const expirationRef = new Date(expYear, expMonth - 1, 1);
        expirationRef.setMonth(expirationRef.getMonth() + 1);
        if (expirationRef <= today) {
          toast({
            title: "Cartão expirado",
            description: "Informe um cartão com validade vigente",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }

        if (cvvDigits.length < 3 || cvvDigits.length > 4) {
          toast({
            title: "CVV inválido",
            description: "Verifique o código de segurança do cartão",
            variant: "destructive",
          });
          setIsProcessing(false);
          return;
        }

        paymentMethodPayload = {
          type: 'credit_card',
          brand: detectCardBrand(digits),
          last4: digits.slice(-4),
          exp_month: expMonth,
          exp_year: expYear,
          holder_name: cardData.holder.trim(),
        };
      }

      const checkoutResponse = await authService.updateCheckoutData({
        person_type: formData.personType,
        cpf: formData.cpf || undefined,
        cnpj: formData.cnpj || undefined,
        address_line1: formData.addressLine1 || undefined,
        address_line2: formData.addressLine2 || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zip_code: formData.zipCode || undefined,
        billing_data: formData.personType === 'juridica' ? {
          company_name: formData.billingData.companyName || undefined,
          email: formData.billingData.email || undefined,
          phone: formData.billingData.phone || undefined,
          address_line1: formData.billingData.addressLine1 || undefined,
          address_line2: formData.billingData.addressLine2 || undefined,
          city: formData.billingData.city || undefined,
          state: formData.billingData.state || undefined,
          zip_code: formData.billingData.zipCode || undefined,
        } : undefined,
        plan_id: plan.id,
        billing_cycle: billingCycle || 'monthly',
        coupon_code: coupon?.valid ? coupon.data?.code : undefined,
        total_amount: total,
        discount_amount: discountAmount,
        payment_method: paymentMethodPayload,
      });

      // Atualiza o perfil para carregar a subscription e destravar os guards de feature
      await refreshProfile();

      setIsProcessing(false);
      toast({
        title: "Pedido finalizado com sucesso!",
        description: `Sua conta foi criada com ${plan.trial_days} dias gratuitos. Aproveite todos os recursos!`,
      });

      if (checkoutResponse.payment_url) {
        window.location.href = checkoutResponse.payment_url;
        return;
      }

      setTimeout(() => {
        navigate(POST_LOGIN_ROUTE);
      }, 1200);

    } catch (error: unknown) {
      setIsProcessing(false);
      console.error('Erro ao atualizar dados do checkout:', error);
      
      const err = error as { response?: { status?: number; data?: { detail?: string } } };
      
      if (err.response?.status === 409 && typeof err.response?.data?.detail === 'string' && 
          err.response.data.detail.startsWith('DUPLICATE_')) {
        
        const parts = err.response.data.detail.split('|');
        const documentType = parts[0].replace('DUPLICATE_', '');
        const message = parts[1] || 'Documento já cadastrado';
        const suggestion = parts[2] || 'Faça login ou recupere sua senha';
        
        setDuplicateDocumentInfo({
          documentType,
          message,
          suggestion
        });
        setShowDuplicateDocumentModal(true);
        return;
      }
      
      const errorMessage = typeof err.response?.data?.detail === 'string' 
        ? err.response.data.detail 
        : "Houve um problema ao atualizar seus dados. Tente novamente.";
        
      toast({
        title: "Erro ao salvar dados",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  if (!plan) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <header className="sticky top-0 z-10" style={{ background: BRAND_HERO_GRADIENT }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/register/plans', { state: { email, name } })}
              className="text-gray-300 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-2">
              <img
                src="/logo-librattum.png"
                alt="Librattum"
                className="h-8"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <Lock className="h-4 w-4 text-emerald-400" />
              Checkout seguro
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="mb-8 flex items-center gap-2 text-sm text-muted-foreground">
          <span>Cadastro</span>
          <span className="text-border">→</span>
          <span>Plano</span>
          <span className="text-border">→</span>
          <span className="text-primary font-medium">Checkout</span>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* User Data Card */}
            <Card className="border-border/60 bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-foreground">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  Dados Pessoais
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Personal Data */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Nome completo</Label>
                    <div className="mt-1.5 p-3 bg-muted/50 border border-border/50 rounded-lg text-foreground">
                      {name || 'Não informado'}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">E-mail</Label>
                    <div className="mt-1.5 p-3 bg-muted/50 border border-border/50 rounded-lg text-foreground">
                      {email || 'Não informado'}
                    </div>
                  </div>
                </div>

                {/* Person Type and CPF */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Tipo de cadastro</Label>
                    <Select 
                      value={formData.personType} 
                      onValueChange={(value) => handleInputChange('personType', value as 'fisica' | 'juridica')}
                    >
                      <SelectTrigger className="mt-1.5 bg-background border-border/60">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fisica">Pessoa Física</SelectItem>
                        <SelectItem value="juridica">Pessoa Jurídica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">
                      CPF {formData.personType === 'juridica' ? 'do Responsável' : ''} *
                    </Label>
                    <Input
                      value={formData.cpf}
                      onChange={(e) => handleInputChange('cpf', e.target.value)}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      className="mt-1.5 border-border/60"
                      required
                    />
                  </div>
                </div>

                {/* CNPJ - PJ Only */}
                {formData.personType === 'juridica' && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">CNPJ da Empresa *</Label>
                      <Input
                        value={formData.cnpj}
                        onChange={(e) => handleInputChange('cnpj', e.target.value)}
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                        className="mt-1.5 border-border/60"
                        required
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Address Card */}
            <Card className="border-border/60 bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-foreground">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  Endereço {formData.personType === 'juridica' ? 'do Responsável' : ''}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Endereço completo</Label>
                  <Input
                    value={formData.addressLine1}
                    onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                    placeholder="Rua, número e bairro"
                    className="mt-1.5 border-border/60"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Complemento</Label>
                  <Input
                    value={formData.addressLine2}
                    onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                    placeholder="Apartamento, sala, etc. (opcional)"
                    className="mt-1.5 border-border/60"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Cidade</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="Cidade"
                      className="mt-1.5 border-border/60"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Estado</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value.toUpperCase())}
                      placeholder="UF"
                      maxLength={2}
                      className="mt-1.5 border-border/60"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">CEP</Label>
                    <Input
                      value={formData.zipCode}
                      onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      placeholder="00000-000"
                      maxLength={9}
                      className="mt-1.5 border-border/60"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Billing Data Card - PJ Only */}
            {formData.personType === 'juridica' && (
              <Card className="border-border/60 bg-card">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3 text-foreground">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    Dados para Faturamento
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Razão Social *</Label>
                      <Input
                        value={formData.billingData.companyName}
                        onChange={(e) => handleBillingInputChange('companyName', e.target.value)}
                        placeholder="Nome da empresa"
                        className="mt-1.5 border-border/60"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">E-mail da Empresa *</Label>
                      <Input
                        type="email"
                        value={formData.billingData.email}
                        onChange={(e) => handleBillingInputChange('email', e.target.value)}
                        placeholder="email@empresa.com"
                        className="mt-1.5 border-border/60"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Telefone da Empresa</Label>
                      <Input
                        value={formData.billingData.phone}
                        onChange={(e) => handleBillingInputChange('phone', e.target.value)}
                        placeholder="(00) 00000-0000"
                        className="mt-1.5 border-border/60"
                        maxLength={PHONE_INPUT_MAX_LENGTH}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Endereço da Empresa</Label>
                    <Input
                      value={formData.billingData.addressLine1}
                      onChange={(e) => handleBillingInputChange('addressLine1', e.target.value)}
                      placeholder="Rua, número e bairro"
                      className="mt-1.5 border-border/60"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Complemento</Label>
                    <Input
                      value={formData.billingData.addressLine2}
                      onChange={(e) => handleBillingInputChange('addressLine2', e.target.value)}
                      placeholder="Sala, andar, etc. (opcional)"
                      className="mt-1.5 border-border/60"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Cidade</Label>
                      <Input
                        value={formData.billingData.city}
                        onChange={(e) => handleBillingInputChange('city', e.target.value)}
                        placeholder="Cidade"
                        className="mt-1.5 border-border/60"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Estado</Label>
                      <Input
                        value={formData.billingData.state}
                        onChange={(e) => handleBillingInputChange('state', e.target.value.toUpperCase())}
                        placeholder="UF"
                        maxLength={2}
                        className="mt-1.5 border-border/60"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">CEP</Label>
                      <Input
                        value={formData.billingData.zipCode}
                        onChange={(e) => handleBillingInputChange('zipCode', e.target.value)}
                        placeholder="00000-000"
                        maxLength={9}
                        className="mt-1.5 border-border/60"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Card Data - If Required */}
            {plan.require_card_on_signup && (
              <Card className="border-border/60 bg-card">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3 text-foreground">
                    <div className="p-2 bg-amber-500/10 rounded-lg">
                      <CreditCard className="h-5 w-5 text-amber-500" />
                    </div>
                    Dados do Cartão
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-600">
                      Seus dados de pagamento são solicitados para ativação da assinatura e são armazenados de forma segura.
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Nome impresso no cartão *</Label>
                      <Input
                        value={cardData.holder}
                        onChange={(e) => handleCardInputChange('holder', e.target.value)}
                        placeholder="Como aparece no cartão"
                        className="mt-1.5 border-border/60"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Número do cartão *</Label>
                      <Input
                        value={cardData.number}
                        onChange={(e) => handleCardInputChange('number', e.target.value)}
                        placeholder="0000 0000 0000 0000"
                        className="mt-1.5 border-border/60"
                        maxLength={23}
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Validade *</Label>
                      <Input
                        value={cardData.expiry}
                        onChange={(e) => handleCardInputChange('expiry', e.target.value)}
                        placeholder="MM/AA"
                        className="mt-1.5 border-border/60"
                        maxLength={5}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">CVV *</Label>
                      <Input
                        value={cardData.cvv}
                        onChange={(e) => handleCardInputChange('cvv', e.target.value)}
                        placeholder="123"
                        className="mt-1.5 border-border/60"
                        maxLength={4}
                      />
                    </div>
                    <div className="flex items-end pb-2">
                      <p className="text-xs text-muted-foreground">
                        Aceitamos Visa, Mastercard, Amex e outros.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Order Summary */}
          <div>
            <Card className="sticky top-24 border-border/60 bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-foreground">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Receipt className="h-5 w-5 text-primary" />
                  </div>
                  Resumo do Pedido
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Selected Plan */}
                <div className="p-4 bg-muted/50 border border-border/50 rounded-xl">
                  <div className="font-semibold text-foreground text-lg">{plan.name}</div>
                  {plan.description && (
                    <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                  )}
                  {plan.trial_days > 0 && (
                    <Badge variant="secondary" className="mt-3 bg-emerald-100 text-emerald-700 border-0">
                      {plan.trial_days} dias grátis
                    </Badge>
                  )}
                  {plan.require_card_on_signup && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-600">
                      <AlertCircle className="h-3.5 w-3.5" />
                      Cartão obrigatório para ativação
                    </div>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="space-y-3 border-t border-border/50 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{billingCycle === 'yearly' ? 'Anualidade' : 'Mensalidade'}</span>
                    <span className="text-foreground">R$ {recurringAmount.toFixed(2).replace('.', ',')}</span>
                  </div>
                  
                  {plan.setup_fee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Taxa de setup</span>
                      <span className="text-foreground">R$ {plan.setup_fee.toFixed(2).replace('.', ',')}</span>
                    </div>
                  )}
                </div>

                {/* Coupon Section */}
                <div className="border-t border-border/50 pt-4 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Tag className="h-4 w-4 text-primary" />
                    Cupom de desconto
                  </div>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Código do cupom"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1 text-sm border-border/60"
                      disabled={validating}
                    />
                    <Button 
                      onClick={validateCoupon} 
                      disabled={validating || !couponCode.trim()}
                      size="sm"
                      className="px-4"
                    >
                      {validating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Aplicar'
                      )}
                    </Button>
                  </div>

                  {coupon && (
                    <div className={cn(
                      'p-3 rounded-lg border text-sm',
                      coupon.valid 
                        ? 'bg-emerald-50 border-emerald-200' 
                        : 'bg-red-50 border-red-200'
                    )}>
                      <div className={cn(
                        'flex items-center gap-2 font-medium',
                        coupon.valid ? 'text-emerald-700' : 'text-red-700'
                      )}>
                        {coupon.valid ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <AlertCircle className="h-4 w-4" />
                        )}
                        {coupon.valid ? 'Cupom aplicado!' : 'Cupom inválido'}
                      </div>
                      <p className={cn(
                        'text-sm mt-1',
                        coupon.valid ? 'text-emerald-600' : 'text-red-600'
                      )}>
                        {coupon.valid 
                          ? `Desconto de ${coupon.data?.discount_type === 'percentage' ? coupon.data.discount_value + '%' : 'R$ ' + coupon.data?.discount_value}`
                          : coupon.reason
                        }
                      </p>
                      {coupon.valid && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={removeCoupon}
                          className="mt-2 h-7 text-xs hover:bg-emerald-100"
                        >
                          Remover cupom
                        </Button>
                      )}
                    </div>
                  )}

                  {coupon?.valid && coupon.data && (
                    <div className="flex justify-between text-sm text-emerald-600 pt-2 border-t border-border/50">
                      <span>Desconto ({coupon.data.code})</span>
                      <span>
                        -{coupon.data.discount_type === 'percentage'
                          ? `${coupon.data.discount_value}%`
                          : `R$ ${coupon.data.discount_value.toFixed(2).replace('.', ',')}`}
                      </span>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="border-t border-border/50 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-foreground">Total</span>
                    <span className="text-2xl font-bold text-primary">
                      R$ {total.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                  {plan.trial_days > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Cobrança apenas após {plan.trial_days} dias de teste
                    </p>
                  )}
                </div>

                {/* Legal Text */}
                <div className="text-center py-3">
                  <p className="text-xs text-muted-foreground">
                    Ao finalizar, você concorda com os{' '}
                    <a 
                      href="/termos"
                      className="text-primary hover:underline"
                    >
                      Termos de Uso
                    </a>
                    {' '}e{' '}
                    <a 
                      href="/privacidade"
                      className="text-primary hover:underline"
                    >
                      Política de Privacidade
                    </a>
                  </p>
                </div>

                {/* Checkout Button */}
                <Button 
                  className="w-full py-6 text-base font-semibold"
                  onClick={proceed}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Processando...
                    </>
                  ) : (
                    'Finalizar pedido'
                  )}
                </Button>

                {/* Security Badge */}
                <div className="text-center pt-4 border-t border-border/50 space-y-3">
                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <Shield className="h-4 w-4 text-emerald-500" />
                    <span>Pagamento 100% seguro e criptografado</span>
                  </div>
                    
                  {/* Powered By Viobank */}
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-xs text-muted-foreground/60">Powered By</span>
                    <a 
                      href="https://www.viobank.com.br" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="transition-opacity hover:opacity-80"
                    >
                      <img 
                        src="/viobank.png" 
                        alt="Viobank" 
                        className="h-6 w-auto"
                      />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Documento já cadastrado em outra conta */}
      <DuplicateDocumentDialog
        open={showDuplicateDocumentModal}
        info={duplicateDocumentInfo}
        onOpenChange={setShowDuplicateDocumentModal}
      />
    </div>
  );
};

export default CheckoutPage;
